import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StakeholderMobileCard = ({ stakeholder, onViewProfile, onEditPermissions, onSendMessage, onAssignTransaction }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getRoleColor = (role) => {
    const colors = {
      'Property Owner': 'bg-primary/10 text-primary',
      'Lawyer': 'bg-accent/10 text-accent',
      'Engineer': 'bg-success/10 text-success',
      'Notary': 'bg-warning/10 text-warning',
      'Broker': 'bg-secondary/10 text-secondary'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-success text-success-foreground',
      'Pending': 'bg-warning text-warning-foreground',
      'Inactive': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            <Image
              src={stakeholder?.avatar}
              alt={stakeholder?.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            {stakeholder?.isOnline && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-card" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{stakeholder?.name}</h3>
            <p className="text-sm text-muted-foreground truncate">{stakeholder?.company}</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-shrink-0"
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
        </Button>
      </div>
      {/* Role and Status */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(stakeholder?.role)}`}>
          {stakeholder?.role}
        </span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(stakeholder?.status)}`}>
          {stakeholder?.status}
        </span>
      </div>
      {/* Quick Stats */}
      <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
        <span>{stakeholder?.transactionCount} transactions</span>
        <span>Last active: {stakeholder?.lastActivity}</span>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="pt-3 border-t border-border space-y-3">
          {/* Contact Information */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={14} className="text-muted-foreground" />
              <span className="text-sm text-foreground">{stakeholder?.email}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={14} className="text-muted-foreground" />
              <span className="text-sm text-foreground">{stakeholder?.phone}</span>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 gap-3 py-3 border-t border-border">
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{stakeholder?.completionRate}%</p>
              <p className="text-xs text-muted-foreground">Completion Rate</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{stakeholder?.avgResponseTime}</p>
              <p className="text-xs text-muted-foreground">Avg Response</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewProfile(stakeholder)}
              iconName="Eye"
              iconPosition="left"
            >
              View Profile
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSendMessage(stakeholder)}
              iconName="MessageSquare"
              iconPosition="left"
            >
              Message
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditPermissions(stakeholder)}
              iconName="Settings"
              iconPosition="left"
            >
              Permissions
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAssignTransaction(stakeholder)}
              iconName="Plus"
              iconPosition="left"
            >
              Assign
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderMobileCard;